import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import api from '../../../../api';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { FaUserCircle } from 'react-icons/fa';

// --- SearchInput moved outside to prevent re-renders ---
const SearchInput = ({ value, onChange, placeholder = 'Search...' }) => {
  const handleChange = useCallback(
    (e) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  return (
    <div className="relative w-full sm:w-64 my-2">
      <input
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        className="border rounded-full pl-4 pr-10 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#1A2B5C]"
      />
      {value && (
        <button onClick={() => onChange('')} className="absolute top-2 right-3 text-gray-400 hover:text-[#EE1D23]">
          <XMarkIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// --- Main Component ---
const EfficiencyTable = ({ filters }) => {
  const { accessToken } = useAuth();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [focusMode, setFocusMode] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBadge, setSelectedBadge] = useState(null);

  useEffect(() => {
    const fetchEfficiency = async () => {
      setIsLoading(true);
      try {
        const baseUrl = getRoundRobinBaseUrl();
        const params = {};
        Object.entries(filters || {}).forEach(([k, v]) => {
          if (v) params[k] = v;
        });
        const res = await api.get(`${baseUrl}/superAdminDashboard/getSuperAdminUsersEfficiency`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params,
        });
        if (res.data.success && Array.isArray(res.data.data)) {
          setData(
            res.data.data.map((item) => ({
              name: item.employeeName || '',
              efficiency: `${item.efficiencyPercentage || '0'}`,
            }))
          );
        } else {
          setData([]);
        }
      } catch (err) {
        console.error(err);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };
    if (filters) fetchEfficiency();
  }, [filters, accessToken]);

  const exportToExcel = () => {
    const sheet = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'Efficiency');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'Efficiency.xlsx');
  };

  const parseNum = (e) => parseFloat(e.replace('%', ''));

  const badgeCounts = useMemo(() => {
    const counts = { EXCELLENT: 0, GOOD: 0, LOW: 0 };
    data.forEach(({ efficiency }) => {
      const num = parseNum(efficiency);
      if (num >= 90) counts.EXCELLENT++;
      else if (num >= 50) counts.GOOD++;
      else counts.LOW++;
    });
    return counts;
  }, [data]);

  const avgEfficiency = useMemo(() => {
    if (!data.length) return '0.00%';
    const sum = data.reduce((acc, { efficiency }) => acc + parseNum(efficiency), 0);
    return `${(sum / data.length).toFixed(2)}%`;
  }, [data]);

  const filtered = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    return data
      .filter(({ name }) => name && name.toLowerCase().includes(keyword))
      .filter(({ efficiency }) => {
        if (!selectedBadge) return true;
        const num = parseNum(efficiency);
        if (selectedBadge === 'EXCELLENT') return num >= 90;
        if (selectedBadge === 'GOOD') return num >= 50 && num < 90;
        if (selectedBadge === 'LOW') return num < 50;
        return true;
      });
  }, [data, searchTerm, selectedBadge]);

  const visibleRows = showAll ? filtered : filtered.slice(0, 5);

  const getStatusBadge = (efficiency) => {
    const num = parseNum(efficiency);
    const base = 'text-xs font-bold px-2 py-0.5 rounded-full shadow-sm tracking-wide';
    if (num >= 90) return <span className={`${base} bg-green-500 text-white`}>EXCELLENT</span>;
    if (num >= 50) return <span className={`${base} bg-orange-400 text-white`}>GOOD</span>;
    return <span className={`${base} bg-[#EE1D23] text-white`}>LOW</span>;
  };

  const renderBadgeFilters = () => (
    <div className="flex flex-wrap gap-2 mt-2">
      {['EXCELLENT', 'GOOD', 'LOW'].map((badge) => {
        const isActive = selectedBadge === badge;
        const bg = badge === 'EXCELLENT' ? 'bg-green-500' : badge === 'GOOD' ? 'bg-orange-400' : 'bg-[#EE1D23]';
        const glow = isActive ? 'ring-2 ring-offset-1 ring-[#1A2B5C] shadow-lg' : 'shadow-sm';
        return (
          <span
            key={badge}
            onClick={() => setSelectedBadge(isActive ? null : badge)}
            className={`text-sm font-semibold px-4 py-1.5 rounded-full cursor-pointer transition ${bg} text-white ${glow}`}
          >
            {badge} ({badgeCounts[badge]})
          </span>
        );
      })}
      {selectedBadge && (
        <button
          onClick={() => setSelectedBadge(null)}
          className="text-sm underline text-[#1A2B5C] hover:text-[#EE1D23] transition ml-2"
        >
          Clear
        </button>
      )}
    </div>
  );

  const TableView = ({ height = 'max-h-[60vh]', fullData = false }) => {
    const rows = fullData ? filtered : visibleRows;
    return (
      <div className={`overflow-y-auto ${height} custom-scroll`}>
        <ul className="divide-y divide-gray-200 text-[15px]">
          <li className="flex justify-between font-semibold text-gray-700 mb-2 px-1">
            <span className="flex-1">Employee</span>
            <span className="w-24 text-right pr-4">Efficiency</span>
          </li>
          {rows.length ? (
            rows.map((user, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="flex items-center justify-between py-3 px-1 hover:bg-slate-50 transition-all"
              >
                <span className="flex items-center gap-4 flex-1">
                  <div className="w-10 h-10 bg-[#1A2B5C] rounded-full text-white flex items-center justify-center shadow-md">
                    <FaUserCircle size={20} />
                  </div>
                  <div className="flex items-center gap-5 flex-wrap">
                    <span className="text-[16px] font-semibold text-[#1A2B5C] truncate">{user.name}</span>
                    {getStatusBadge(user.efficiency)}
                  </div>
                </span>
                <span className="w-24 text-right pr-4 text-[#EE1D23] font-semibold text-[15px]">{user.efficiency}</span>
              </motion.li>
            ))
          ) : (
            <li className="text-center text-gray-500 italic py-6">No records found</li>
          )}
        </ul>
      </div>
    );
  };

  const LoadingDots = () => (
    <div className="flex justify-center items-center space-x-2 py-6">
      <div className="w-4 h-4 bg-[#1A2B5C] rounded-full animate-bounce"></div>
      <div className="w-4 h-4 bg-[#EE1D23] rounded-full animate-bounce [animation-delay:0.2s]"></div>
      <div className="w-4 h-4 bg-[#1A2B5C] rounded-full animate-bounce [animation-delay:0.4s]"></div>
    </div>
  );

  return (
    <>
      {/* Main View */}
      <div className="relative bg-white bg-opacity-90 backdrop-blur-xl rounded-3xl p-6 shadow-xl border-t-4 border-[#EE1D23]">
        <div className="flex flex-col gap-3 mb-4">
          {/* Top row: title + search + buttons */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Left: Icon + Title */}
            <div className="flex items-center gap-3">
              <div className="bg-[#EE1D23] p-2 rounded-full text-white">
                <FaUserCircle size={20} />
              </div>
              <h2 className="text-xl font-bold text-[#1A2B5C] whitespace-nowrap">Users Efficiency</h2>
            </div>

            {/* Middle: Search bar */}
            <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Search employee..." />

            {/* Right: Export & Focus */}
            <div className="flex gap-2">
              <button
                onClick={exportToExcel}
                className="bg-gradient-to-r from-[#1A2B5C] to-[#EE1D23] px-4 py-2 text-sm text-white rounded-full font-semibold shadow hover:opacity-90"
              >
                Export
              </button>
              <button
                onClick={() => setFocusMode(true)}
                className="px-4 py-2 text-sm font-semibold border border-[#1A2B5C] rounded-full text-[#1A2B5C] hover:bg-[#1A2B5C] hover:text-white"
              >
                Focus Mode
              </button>
            </div>
          </div>

          {/* Badge Filters */}
          {renderBadgeFilters()}
        </div>

        {isLoading ? <LoadingDots /> : <TableView />}
        {!isLoading && filtered.length > 5 && (
          <div className="text-center mt-4">
            <button
              onClick={() => setShowAll(!showAll)}
              className="text-sm font-semibold text-[#1A2B5C] underline hover:text-[#EE1D23]"
            >
              {showAll ? 'Show Less' : `Show ${filtered.length - 5} More`}
            </button>
          </div>
        )}
        <div className="pt-3 text-right text-sm font-bold text-[#1A2B5C]">Total Avg Efficiency: {avgEfficiency}</div>
      </div>

      {/* Focus Mode */}
      {focusMode && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-md overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="w-full max-w-6xl mx-auto bg-white rounded-3xl p-8 relative animate-slideZoomUp">
              <button
                onClick={() => setFocusMode(false)}
                className="absolute top-5 right-5 bg-white shadow-md rounded-full p-2 text-gray-600 hover:text-[#EE1D23] transition-transform hover:scale-110"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
              <div className="flex flex-col gap-3 mb-6">
                {/* Title + Search in same row */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-[#EE1D23] p-2 rounded-full text-white">
                      <FaUserCircle size={20} />
                    </div>
                    <h2 className="text-2xl font-bold text-[#1A2B5C]">Users Efficiency – Focus View</h2>
                  </div>
                  <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Search employee..." />
                </div>

                <p className="text-sm text-gray-500 ml-[2.5rem] sm:ml-0">
                  {selectedBadge ?? 'All'} – {filtered.length} records
                </p>

                {/* Badge Filters below */}
                {renderBadgeFilters()}
              </div>

              {isLoading ? <LoadingDots /> : <TableView height="max-h-[65vh]" fullData />}
              <div className="pt-4 text-right text-sm font-bold text-[#1A2B5C]">Total Avg Efficiency: {avgEfficiency}</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EfficiencyTable;
